import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Bell, AlertTriangle, Clock, CheckCircle, 
  Phone, Mail, User, Calendar, Syringe,
  RefreshCw, Filter, Search, XCircle, Send, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

const AlertsPage = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState('all');
  const [filterDias, setFilterDias] = useState('all');
  const [searchDoc, setSearchDoc] = useState('');
  const [patientHistory, setPatientHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [notificationLogs, setNotificationLogs] = useState([]);

  const canSendNotifications = user?.permissions?.notifications?.includes('send');

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEstado && filterEstado !== 'all') {
        params.append('estado', filterEstado);
      }
      if (filterDias && filterDias !== 'all') {
        params.append('dias_limite', filterDias);
      }

      const [alertsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/alerts?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/alerts/summary`, { withCredentials: true })
      ]);

      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Error al cargar las alertas');
    } finally {
      setLoading(false);
    }
  }, [filterEstado, filterDias]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleGenerateAlerts = async () => {
    try {
      const response = await axios.post(`${API}/alerts/generate`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchAlerts();
    } catch (error) {
      toast.error('Error al generar alertas');
    }
  };

  const handleUpdateStatus = async (alertId, newStatus) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/status?estado=${newStatus}`, {}, {
        withCredentials: true
      });
      toast.success('Estado actualizado');
      fetchAlerts();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleSearchPatient = async () => {
    if (!searchDoc.trim()) {
      toast.error('Ingrese un documento');
      return;
    }

    try {
      const response = await axios.get(`${API}/patients/${searchDoc}/history`, {
        withCredentials: true
      });
      setPatientHistory(response.data);
      setShowHistory(true);
    } catch (error) {
      toast.error('Paciente no encontrado');
    }
  };

  const handleSendWhatsApp = async (alertId) => {
    setSendingId(alertId);
    try {
      const response = await axios.post(`${API}/notifications/send-reminder/${alertId}`, {}, {
        withCredentials: true
      });
      toast.success('Recordatorio enviado por WhatsApp');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar WhatsApp');
    } finally {
      setSendingId(null);
    }
  };

  const handleSendBulkWhatsApp = async () => {
    try {
      const response = await axios.post(`${API}/notifications/send-bulk?dias_limite=3`, {}, {
        withCredentials: true
      });
      toast.success(`${response.data.enviados} recordatorios enviados`);
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios');
    }
  };

  const getStatusBadge = (estado, diasRestantes) => {
    if (estado === 'COMPLETADO') {
      return <Badge className="bg-emerald-100 text-emerald-700">Completado</Badge>;
    }
    if (estado === 'VENCIDO' || diasRestantes < 0) {
      return <Badge className="bg-red-100 text-red-700">Vencido</Badge>;
    }
    if (diasRestantes <= 7) {
      return <Badge className="bg-amber-100 text-amber-700">Urgente</Badge>;
    }
    if (estado === 'NOTIFICADO') {
      return <Badge className="bg-blue-100 text-blue-700">Notificado</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Pendiente</Badge>;
  };

  const getUrgencyColor = (dias) => {
    if (dias < 0) return 'border-l-red-500 bg-red-50/50';
    if (dias <= 7) return 'border-l-amber-500 bg-amber-50/50';
    if (dias <= 30) return 'border-l-blue-500';
    return 'border-l-slate-300';
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Alertas de Vacunación</h1>
            <p className="text-slate-500 mt-1">Seguimiento de dosis pendientes</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={fetchAlerts}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
            {canSendNotifications && (
              <Button 
                variant="outline"
                onClick={handleSendBulkWhatsApp}
                className="gap-2 text-green-600 border-green-200 hover:bg-green-50"
                data-testid="send-bulk-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Enviar Recordatorios
              </Button>
            )}
            <Button 
              onClick={handleGenerateAlerts}
              className="btn-accent gap-2"
              data-testid="generate-alerts-btn"
            >
              <Bell className="w-4 h-4" />
              Generar Alertas
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="metric-card border-l-red-500" data-testid="urgent-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Urgentes (7 días)
                  </p>
                  <p className="text-2xl font-bold text-red-600 mt-2 font-mono">
                    {summary.urgentes_7_dias || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </Card>

            <Card className="metric-card border-l-amber-500" data-testid="upcoming-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Próximas (30 días)
                  </p>
                  <p className="text-2xl font-bold text-amber-600 mt-2 font-mono">
                    {summary.proximas_30_dias || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </Card>

            <Card className="metric-card border-l-slate-500" data-testid="overdue-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Vencidas
                  </p>
                  <p className="text-2xl font-bold text-slate-600 mt-2 font-mono">
                    {summary.vencidas || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </Card>

            <Card className="metric-card border-l-emerald-500" data-testid="completed-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Completadas
                  </p>
                  <p className="text-2xl font-bold text-emerald-600 mt-2 font-mono">
                    {summary.por_estado?.COMPLETADO || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="alerts" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="alerts" className="gap-2">
              <Bell className="w-4 h-4" />
              Alertas
            </TabsTrigger>
            <TabsTrigger value="search" className="gap-2">
              <Search className="w-4 h-4" />
              Buscar Paciente
            </TabsTrigger>
          </TabsList>

          <TabsContent value="alerts">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4">
                  <div className="w-48">
                    <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                      Estado
                    </Label>
                    <Select value={filterEstado} onValueChange={setFilterEstado}>
                      <SelectTrigger data-testid="filter-estado">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                        <SelectItem value="NOTIFICADO">Notificado</SelectItem>
                        <SelectItem value="COMPLETADO">Completado</SelectItem>
                        <SelectItem value="VENCIDO">Vencido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                      Días Restantes
                    </Label>
                    <Select value={filterDias} onValueChange={setFilterDias}>
                      <SelectTrigger data-testid="filter-dias">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="7">Próximos 7 días</SelectItem>
                        <SelectItem value="14">Próximos 14 días</SelectItem>
                        <SelectItem value="30">Próximos 30 días</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alerts List */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="loading-spinner w-10 h-10"></div>
              </div>
            ) : alerts.length === 0 ? (
              <Card className="p-12 text-center">
                <Bell className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  No hay alertas
                </h3>
                <p className="text-slate-500 mb-6">
                  Las alertas se generan automáticamente al registrar vacunaciones
                </p>
                <Button onClick={handleGenerateAlerts} className="btn-accent">
                  Generar desde historial
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <Card 
                    key={alert.id}
                    className={`border-l-4 ${getUrgencyColor(alert.dias_restantes)} hover:shadow-md transition-shadow`}
                    data-testid={`alert-card-${alert.id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900">
                              {alert.paciente_nombre}
                            </h3>
                            {getStatusBadge(alert.estado, alert.dias_restantes)}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-slate-600">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="font-mono">{alert.paciente_id}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Syringe className="w-4 h-4 text-slate-400" />
                              <span>{alert.vacuna}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span>
                                {alert.proxima_dosis}: <span className="font-mono">{alert.fecha_proxima}</span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className={`font-semibold ${
                                alert.dias_restantes < 0 ? 'text-red-600' :
                                alert.dias_restantes <= 7 ? 'text-amber-600' : 'text-slate-600'
                              }`}>
                                {alert.dias_restantes < 0 
                                  ? `${Math.abs(alert.dias_restantes)} días vencido`
                                  : `${alert.dias_restantes} días`
                                }
                              </span>
                            </div>
                          </div>

                          {(alert.paciente_telefono || alert.paciente_email) && (
                            <div className="flex gap-4 mt-2 text-sm text-slate-500">
                              {alert.paciente_telefono && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <span>{alert.paciente_telefono}</span>
                                </div>
                              )}
                              {alert.paciente_email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  <span>{alert.paciente_email}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          {alert.estado !== 'COMPLETADO' && (
                            <>
                              {canSendNotifications && alert.paciente_telefono && !alert.whatsapp_sent && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => handleSendWhatsApp(alert.id)}
                                  disabled={sendingId === alert.id}
                                  data-testid={`whatsapp-btn-${alert.id}`}
                                >
                                  {sendingId === alert.id ? (
                                    <div className="loading-spinner w-4 h-4"></div>
                                  ) : (
                                    <MessageCircle className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {alert.whatsapp_sent && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <MessageCircle className="w-3 h-3 mr-1" />
                                  Enviado
                                </Badge>
                              )}
                              {alert.estado !== 'NOTIFICADO' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUpdateStatus(alert.id, 'NOTIFICADO')}
                                  data-testid={`notify-btn-${alert.id}`}
                                >
                                  Notificado
                                </Button>
                              )}
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleUpdateStatus(alert.id, 'COMPLETADO')}
                                data-testid={`complete-btn-${alert.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Completar
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Buscar Historial de Paciente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <div className="flex-1">
                    <Input
                      placeholder="Ingrese RUT o documento..."
                      value={searchDoc}
                      onChange={(e) => setSearchDoc(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchPatient()}
                      className="input-clinical"
                      data-testid="search-patient-input"
                    />
                  </div>
                  <Button onClick={handleSearchPatient} className="gap-2" data-testid="search-patient-btn">
                    <Search className="w-4 h-4" />
                    Buscar
                  </Button>
                </div>

                {patientHistory && (() => {
                  const alertasPendientes = patientHistory.alertas
                    ? patientHistory.alertas.filter(a => a.estado !== 'COMPLETADO')
                    : [];
                  return (
                  <div className="space-y-6">
                    {/* Patient Info */}
                    <div className="p-4 rounded-lg bg-slate-50">
                      <h3 className="font-semibold text-slate-900 mb-3">
                        {patientHistory.paciente.nombre}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Documento:</span>
                          <p className="font-mono">{patientHistory.paciente.documento}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Edad:</span>
                          <p>{patientHistory.paciente.edad}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Teléfono:</span>
                          <p>{patientHistory.paciente.telefono || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Email:</span>
                          <p>{patientHistory.paciente.email || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Vaccination History */}
                    <div>
                      <h4 className="font-semibold text-slate-700 mb-3">
                        Historial de Vacunaciones ({patientHistory.vacunaciones.length})
                      </h4>
                      <div className="table-wrapper">
                        <table className="w-full">
                          <thead className="table-header">
                            <tr>
                              <th className="p-3">Fecha</th>
                              <th className="p-3">Vacuna</th>
                              <th className="p-3">Dosis</th>
                              <th className="p-3">Tipo</th>
                              <th className="p-3">Lote</th>
                            </tr>
                          </thead>
                          <tbody>
                            {patientHistory.vacunaciones.map((v, idx) => (
                              <tr key={v.id || `hist-${idx}`} className="table-row">
                                <td className="p-3 font-mono text-sm">{v.fecha_vacunacion}</td>
                                <td className="p-3">{v.vacuna}</td>
                                <td className="p-3">{v.dosis}</td>
                                <td className="p-3">{v.tipo_vacunacion}</td>
                                <td className="p-3 font-mono text-sm">{v.lote_serie || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pending Alerts */}
                    {patientHistory.alertas.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-3">
                          Alertas Pendientes ({alertasPendientes.length})
                        </h4>
                        <div className="space-y-2">
                          {alertasPendientes.map((a, idx) => (
                            <div key={`${a.vacuna}-${a.fecha_proxima}-${idx}`} className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium">{a.vacuna}</span>
                                  <span className="text-slate-500 mx-2">→</span>
                                  <span>{a.proxima_dosis}</span>
                                </div>
                                <span className="font-mono text-amber-700">{a.fecha_proxima}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AlertsPage;
